import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

const REQUEST_ID_KEY = "requestId";
const QUERY_KEY = "q";

/**
 * 신청 상품 내역 모달의 열림 상태·검색어를 URL 쿼리스트링에 둔다.
 * 링크 하나로 "등록 신청 내역 → 그 요청의 신청 상품 내역 → 검색어로 필터된 목록"까지 재현하기 위한 것.
 *
 * requestId의 존재가 곧 열림 상태다 — 이 페이지에서 URL과 묶는 모달이 하나뿐이라 판별용 키를 따로 두지 않는다.
 */
export default function useRequestProductsModalParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  // 양의 정수가 아니면 닫힌 것으로 본다.
  // 상세 조회 훅이 enabled: Boolean(requestId)라서 0/NaN을 그대로 넘기면 스피너가 영원히 돈다.
  const rawRequestId = searchParams.get(REQUEST_ID_KEY);
  const parsedRequestId = Number(rawRequestId);
  const requestId =
    rawRequestId !== null &&
    rawRequestId !== "" &&
    Number.isInteger(parsedRequestId) &&
    parsedRequestId > 0
      ? parsedRequestId
      : null;

  const query = searchParams.get(QUERY_KEY) ?? "";

  const open = useCallback(
    (id: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(REQUEST_ID_KEY, String(id));
        next.delete(QUERY_KEY);
        return next;
      });
    },
    [setSearchParams]
  );

  const close = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(REQUEST_ID_KEY);
      next.delete(QUERY_KEY);
      return next;
    });
  }, [setSearchParams]);

  // 검색어는 커밋 시점에만 URL에 반영하고 히스토리는 쌓지 않는다(타이핑마다 뒤로가기가 늘어나지 않게).
  const commitQuery = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) {
            next.set(QUERY_KEY, value);
          } else {
            next.delete(QUERY_KEY);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return { requestId, query, open, close, commitQuery };
}
